import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Pencil, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

/**
 * ClientSiteMembership — inline card + editor for a service user's Primary
 * Site and Additional Sites. Uses the existing sites collection (no
 * duplication) and the generic clients module PUT endpoint. Displays as a
 * compact strip; managers/admins can click Edit to open the picker.
 */
export default function ClientSiteMembership({ client, canManage, onSaved }) {
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(false);
  const [primary, setPrimary] = useState(client?.site_id || "");
  const [additional, setAdditional] = useState(client?.additional_site_ids || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/sites").then((r) => setSites(r.data || [])).catch(() => setSites([]));
  }, []);
  useEffect(() => {
    if (open) {
      setPrimary(client?.site_id || "");
      setAdditional(client?.additional_site_ids || []);
    }
  }, [open, client]);

  const siteName = (id) => sites.find((s) => s.id === id)?.name || null;
  const primaryName = siteName(client?.site_id);
  const additionalNames = (client?.additional_site_ids || [])
    .map(siteName)
    .filter(Boolean);

  const toggleAdditional = (id) => {
    setAdditional((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const save = async () => {
    if (!primary && additional.length > 0) {
      toast.error("Set a Primary Site before adding Additional Sites."); return;
    }
    if (additional.includes(primary)) {
      toast.error("A site cannot be both Primary and Additional."); return;
    }
    setSaving(true);
    try {
      const r = await api.put(`/clients/${client.id}`, {
        data: {
          site_id: primary || null,
          additional_site_ids: additional,
        },
      });
      toast.success("Site membership updated");
      onSaved?.(r.data);
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const totalCount = (primaryName ? 1 : 0) + additionalNames.length;

  return (
    <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="client-site-membership">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold inline-flex items-center gap-1">
        <Building2 className="w-3 h-3" /> Sites
      </div>
      {totalCount === 0 ? (
        <span className="text-[11px] text-slate-400 italic" data-testid="client-site-none">Not assigned to any site</span>
      ) : (
        <>
          {primaryName && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border border-sky-300 bg-sky-50 text-sky-800" data-testid="client-primary-site">
              <span className="text-[9px] uppercase tracking-widest opacity-80">Primary</span>
              {primaryName}
            </span>
          )}
          {additionalNames.map((n) => (
            <span key={n} className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border border-slate-300 bg-slate-50 text-slate-700" data-testid={`client-additional-site-${n}`}>
              {n}
            </span>
          ))}
        </>
      )}
      {canManage && (
        <button
          onClick={() => setOpen(true)}
          className="text-[11px] text-slate-500 hover:text-sky-700 inline-flex items-center gap-1"
          data-testid="btn-edit-client-sites"
        >
          <Pencil className="w-3 h-3" /> {totalCount === 0 ? "Assign sites" : "Edit"}
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="client-sites-dialog">
          <DialogHeader>
            <DialogTitle>Site membership</DialogTitle>
            <DialogDescription>
              Pick one Primary Site and any Additional Sites this service user visits. Only shifts booked at these sites can be scheduled for them.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div>
              <Label className="text-xs font-medium">Primary Site</Label>
              <Select value={primary || "__none"} onValueChange={(v) => setPrimary(v === "__none" ? "" : v)}>
                <SelectTrigger data-testid="client-primary-site-select"><SelectValue placeholder="Not assigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Not assigned</SelectItem>
                  {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">Additional Sites</Label>
              <div className="mt-1 border border-slate-200 rounded-md p-2 max-h-56 overflow-y-auto space-y-1" data-testid="client-additional-sites-list">
                {sites.length === 0 && <div className="text-[11px] text-slate-500 italic px-1">No sites configured yet.</div>}
                {sites.filter((s) => s.id !== primary).map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm px-1 py-0.5 rounded hover:bg-slate-50 cursor-pointer" data-testid={`client-additional-site-toggle-${s.id}`}>
                    <Checkbox checked={additional.includes(s.id)} onCheckedChange={() => toggleAdditional(s.id)} />
                    <span className="flex-1">{s.name}</span>
                  </label>
                ))}
              </div>
              {additional.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {additional.map((id) => (
                    <span key={id} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-slate-100 border border-slate-300">
                      {siteName(id) || id}
                      <button type="button" onClick={() => toggleAdditional(id)} className="text-slate-400 hover:text-red-700" aria-label={`Remove ${siteName(id)}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-sky-700 hover:bg-sky-800" data-testid="btn-save-client-sites">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
